import { Action } from 'redux';
import { put, select } from '@redux-saga/core/effects';

import { CheckoutActions, CheckoutActionTypes } from '../checkout.types';
import { ACTION_CREATOR_NAME, errorToBasketErrorModel } from 'helpers';
import { AppState, ErrorAction } from '../../root.types';
import { loadBasketSaga } from 'store/basket/actions';
import { PaymentTypes } from 'models/Payment';
import { loadPaymentSaga } from 'store/payment/actions';
import { PaymentActionTypes } from '../../payment';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { initIfcApplicationDataSaga, loadQuestionSetSaga } from './finance';
import { BasketErrorModel, DEFAULT_ERROR_TEXT } from 'models/Errors';

export interface InitCheckoutPageAction extends Action<CheckoutActionTypes.INIT_CHECKOUT_PAGE> {
}

export interface InitCheckoutPageActionStart extends Action<CheckoutActionTypes.INIT_CHECKOUT_PAGE_START> {
}

export interface InitCheckoutPageActionSuccess extends Action<CheckoutActionTypes.INIT_CHECKOUT_PAGE_SUCCESS> {
}

export interface InitCheckoutPageActionError extends ErrorAction<CheckoutActionTypes.INIT_CHECKOUT_PAGE_ERROR> {
}

export const initCheckoutPage: () => InitCheckoutPageAction = () => ({
    type: CheckoutActionTypes.INIT_CHECKOUT_PAGE
});
initCheckoutPage[ACTION_CREATOR_NAME] = 'initCheckoutPage';

export type InitCheckoutPageFunc = (...args: Parameters<typeof initCheckoutPage>) => void;

export function* initCheckoutPageSaga(): BasketSagaReturnType {
    try {
        let state: AppState = yield select();
        const shouldLoadData = !state.basketState.basket?.entries.length
            || state.paymentState.paymentDetails.paymentType === PaymentTypes.None
            || !state.checkoutState.ifcQuestionSet;

        if (shouldLoadData) {
            yield put<CheckoutActions>({
                type: CheckoutActionTypes.INIT_CHECKOUT_PAGE_START
            });

            if (!state.basketState.basket) {
                yield callBasketSaga(loadBasketSaga);
            }

            if (state.paymentState.paymentDetails.paymentType === PaymentTypes.None) {
                yield callBasketSaga(loadPaymentSaga, {
                    type: PaymentActionTypes.LOAD_PAYMENT
                });
            }

            yield callBasketSaga(loadQuestionSetSaga);
            state = yield select();

            yield callBasketSaga(initIfcApplicationDataSaga, {
                type: CheckoutActionTypes.INIT_IFC_APPLICATION_DATA,
                customerDeliveryData: null,
                questionSet: state.checkoutState.ifcQuestionSet!,
            });
        }

        yield validateBasket();

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_CHECKOUT_PAGE_SUCCESS
        });

        return true;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.INIT_CHECKOUT_PAGE_ERROR,
            error: errorToBasketErrorModel(error, 'initCheckoutPageSaga')
        });

        return false;
    }
}

function* validateBasket(): Generator<any, void, any> {
    const state = yield select();

    if (state.basketState.basket!.totalPriceWithTax.value !== state.paymentState.paymentDetails.total) {
        throw new BasketErrorModel(DEFAULT_ERROR_TEXT,
            'Basket total is not equal to saved payment details total',
            'initCheckoutPageSaga',
            [
                { key: 'basketTotal', value: state.basketState.basket?.totalPriceWithTax?.value ?? null },
                { key: 'paymentDetailsTotal', value: state.paymentState.paymentDetails?.total ?? null }
            ]);
    }

    if (state.paymentState.paymentDetails.paymentType === PaymentTypes.None) {
        throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'Saved payment type is empty', 'initCheckoutPageSaga');
    }

    if (!state.basketState.basket?.entries.length) {
        throw new BasketErrorModel(DEFAULT_ERROR_TEXT, 'Basket is empty', 'initCheckoutPageSaga');
    }
}